import { useTranslation } from 'react-i18next'

import { CAMPAIGN_CHANNELS } from '#/entities/campaign'
import type { CampaignChannel } from '#/entities/campaign'
import { Button } from '#/shared/ui/button'

import { useCampaignComposer } from '../model/use-campaign-composer'
import type {
  CampaignComposerSeed,
  CampaignStepDraft,
} from '../model/use-campaign-composer'
import { CampaignEmailComposer } from './campaign-email-composer'
import { CampaignSmsComposer } from './campaign-sms-composer'
import { CampaignStepWaitRow } from './campaign-step-wait-row'
import { CampaignTaskComposer } from './campaign-task-composer'

interface CampaignStepComposerProps {
  channel: CampaignChannel
  /** 1-based position the new step will take. */
  position: number
  /** "2 days after step 4 · only if no reply, and no booking". */
  timing: string
  onCancel: () => void
  onSave?: (draft: CampaignStepDraft) => void
  saving?: boolean
  /** Step 1's subject, for the "reply in thread" row. */
  threadSubject?: string
  /** Opens on an existing step's content instead of a blank one. */
  seed?: CampaignComposerSeed
  /** Heading and save wording differ between adding and editing. */
  title?: string
  saveLabel?: string
}

/**
 * Writing the message for a new step — the second half of adding one, after the
 * picker has settled the channel and the timing.
 *
 * The three channels share this shell and nothing else: SMS is one box beside a
 * live preview, email is a thread decision, a subject, and a body, and a task
 * is a title and who does it — nothing is sent. Forcing them into one form
 * would mean hiding most of it whichever one is open.
 */
export function CampaignStepComposer({
  channel,
  position,
  timing,
  onCancel,
  onSave,
  saving = false,
  threadSubject,
  seed,
  title,
  saveLabel,
}: CampaignStepComposerProps) {
  const { t } = useTranslation()
  const composer = useCampaignComposer({ channel, onCancel, seed })

  return (
    <section className="overflow-hidden rounded-xl border-2 border-campaign-running bg-white">
      <header className="flex flex-wrap items-center gap-2.5 px-3.5 py-3">
        <h3 className="text-sm font-bold text-announce-ink">
          {title ?? t('campaigns.composer.title', { position })}
        </h3>
        <span className="rounded-md border border-campaign-running bg-avatar-blue-soft px-2 py-1 text-xs font-medium text-campaign-running">
          {t(`campaigns.channel.${channel}`)}
        </span>
        <span className="text-xs text-warm-500">{timing}</span>
        <span className="hidden h-px flex-1 sm:block" />
        <Button variant="outline" size="sm" onClick={composer.cancel}>
          {t('campaigns.composer.cancel')}
        </Button>
        <Button
          size="sm"
          pressEffect
          // A task has no body to write — its title is what makes it savable,
          // and the service rejects one without a `subject`.
          disabled={saving || composer.saveBlocked}
          onClick={() => onSave?.(composer.draft)}
        >
          {saveLabel ?? t('campaigns.composer.save')}
        </Button>
      </header>
      <div className="h-px w-full bg-gray-200" />
      {composer.waitOptions.length > 0 && (
        <>
          <CampaignStepWaitRow composer={composer} />
          <div className="h-px w-full bg-gray-200" />
        </>
      )}
      {channel === CAMPAIGN_CHANNELS.task && (
        <CampaignTaskComposer composer={composer} />
      )}
      {channel === CAMPAIGN_CHANNELS.sms && (
        <CampaignSmsComposer composer={composer} />
      )}
      {channel === CAMPAIGN_CHANNELS.email && (
        <CampaignEmailComposer
          composer={composer}
          threadSubject={threadSubject}
        />
      )}
    </section>
  )
}
